import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { EmptyState, LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { SkuMeta } from '@/components/SkuMeta';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { toScaled, plainQty } from './shared';
import { ResolveShortfallDialog } from './ResolveShortfallDialog';
import type { OrderShortfall, ShortfallAction } from '@/api/types';

/** Order lines that cannot be completed - short pick, or stock lost under a reservation. */
export function ShortfallsTab(): JSX.Element {
  const { t } = useLanguage();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('ORDERS.WRITE');
  const result = useApi(() => endpoints.orderShortfalls(), []);
  const [active, setActive] = useState<{ line: OrderShortfall; action: ShortfallAction } | null>(null);

  if (result.status === 'loading') return <LoadingState />;
  if (result.status === 'error') return <ErrorBanner error={result.error} onRetry={result.reload} />;
  const lines = result.data.lines;

  return (
    <div>
      {active && (
        <ResolveShortfallDialog
          key={`${active.line.line_id}:${active.action}`}
          line={active.line}
          action={active.action}
          onClose={() => setActive(null)}
          onDone={() => {
            setActive(null);
            result.reload();
          }}
        />
      )}
      {lines.length === 0 ? (
        <EmptyState title={t('exc.shortEmpty')} />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('exc.colOrder')}</th>
                <th>{t('exc.colSeller')}</th>
                <th>{t('exc.colProduct')}</th>
                <th>{t('exc.colOrdered')}</th>
                <th>{t('exc.colPicked')}</th>
                <th>{t('exc.colReserved')}</th>
                <th>{t('exc.colMissing')}</th>
                {canWrite && <th>{t('exc.colActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const picked = toScaled(l.qty_picked) > BigInt(0);
                return (
                  <tr key={l.line_id}>
                    <td>
                      <Link to={`/shipments/${l.order_id}`}>{l.number}</Link> <StatusBadge value={l.order_state} />
                      {!l.manual && <span className="badge neutral"> {t('exc.marketplaceBadge')}</span>}
                    </td>
                    <td>{l.owner_name}</td>
                    <td>
                      {l.sku_code} — {l.sku_name}
                      <SkuMeta code={l.sku_code} article={l.article} size={l.size} color={l.color} barcodes={l.barcodes} />
                    </td>
                    <td>{plainQty(l.qty_ordered)}</td>
                    <td>{plainQty(l.qty_picked)}</td>
                    <td>{plainQty(l.qty_held)}</td>
                    <td>
                      <strong>{plainQty(l.shortfall)}</strong>
                    </td>
                    {canWrite && (
                      <td>
                        <div className="action-row">
                          <button type="button" className="primary" onClick={() => setActive({ line: l, action: 'REALLOCATE' })}>
                            {t('exc.reallocate')}
                          </button>
                          <button
                            type="button"
                            disabled={!l.manual}
                            aria-describedby={!l.manual ? `reduce-why-${l.line_id}` : undefined}
                            onClick={() => setActive({ line: l, action: 'REDUCE' })}
                          >
                            {t('exc.reduce')}
                          </button>
                          {!l.manual && (
                            <span id={`reduce-why-${l.line_id}`} className="field-hint">
                              {t('exc.reduceDisabled')}
                            </span>
                          )}
                          <button
                            type="button"
                            className="danger"
                            disabled={picked}
                            aria-describedby={picked ? `cancel-why-${l.line_id}` : undefined}
                            onClick={() => setActive({ line: l, action: 'CANCEL_LINE' })}
                          >
                            {t('exc.cancelLine')}
                          </button>
                          {picked && (
                            <span id={`cancel-why-${l.line_id}`} className="field-hint">
                              {t('exc.cancelLineDisabled')}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
